import { FC, useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import Button from "~/components/Button/Button";
import OrderContext from "~/contexts/OrderContext";
import MenuContext from "~/contexts/MenuContext";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import { CartLine } from "~/entities/orderEntities";
import { MenuTabItem } from "~/entities/menuEntities";
import {
  choiceRequiredForProduct,
  generateLine,
  getModifiersList,
  getTotalPrice,
} from "~/helpers/cartHelpers";
import {
  countOf,
  copyLine,
  Deal,
  DealPicks,
  DealSlot,
  pickCount,
  picksComplete,
  picksSummary,
  slotsNeeded,
} from "~/helpers/dealHelpers";
import { offerSavingBackground, offerSavingColour } from "~/helpers/offerTheme";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import MinusIcon from "~/icons/MinusIcon";
import PlusIcon from "~/icons/PlusIcon";
import BackChevronIcon from "~/icons/BackChevronIcon";
import CloseIcon from "~/icons/CloseIcon";

import styles from "./DealBuilder.module.css";

type DealBuilderProps = {
  deal: Deal;
  initialPicks: DealPicks;
  // Cart lines the picks replace in the same save (the deal being edited, or loose items used as picks)
  replaceLineIds: string[] | null;
  isEdit: boolean;
  onClose: VoidFunction;
};

const DealBuilder: FC<DealBuilderProps> = ({
  deal,
  initialPicks,
  replaceLineIds,
  isEdit,
  onClose,
}) => {
  const { addOrderLines, orderSource, readonlyCart } = useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);

  const [picks, setPicks] = useState<DealPicks>(initialPicks);
  const [qty, setQty] = useState(1);
  const [optionsFor, setOptionsFor] = useState<{
    slot: DealSlot;
    line: CartLine;
    replace: boolean;
  } | null>(null);

  const firstOpenSlot = (p: DealPicks) =>
    deal.slots.find((s) => p[s.index].length < s.qty)?.index ?? null;
  // Editing a finished deal opens on its first slot
  const [activeSlot, setActiveSlot] = useState<number | null>(
    () => firstOpenSlot(initialPicks) ?? 0,
  );

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, []);

  const complete = picksComplete(deal, picks);
  const summary = picksSummary(deal, picks);
  const needed = slotsNeeded(deal);
  const chosen = pickCount(picks);

  const brand = template.global.button.primaryButtonColour;
  const savingColour = offerSavingColour(template);
  const savingBackground = offerSavingBackground(template);

  const setSlotPicks = (slot: DealSlot, lines: CartLine[]) => {
    const next = picks.map((x, i) => (i === slot.index ? lines : x));
    setPicks(next);
    // Move on once this slot is full
    if (lines.length >= slot.qty) {
      // Stay on this slot once everything is chosen, so the choice can still be changed
      setActiveSlot(firstOpenSlot(next) ?? slot.index);
    }
  };

  const addPick = (slot: DealSlot, line: CartLine, replace: boolean) => {
    const lines = replace ? [line] : [...picks[slot.index], line];
    setSlotPicks(slot, lines.slice(-slot.qty));
  };

  const choose = (slot: DealSlot, menuItem: MenuTabItem, replace: boolean) => {
    if (!menu) {
      return;
    }
    const line = generateLine(menuItem, menu.code);
    if (choiceRequiredForProduct(menuItem.productData, orderSource)) {
      setOptionsFor({ slot, line, replace });
    } else {
      addPick(slot, line, replace);
    }
  };

  const removeOne = (slot: DealSlot, productCode: string) => {
    const lines = [...picks[slot.index]];
    const i = lines.map((x) => x.item.productCode).lastIndexOf(productCode);
    if (i >= 0) {
      lines.splice(i, 1);
      setPicks(picks.map((x, n) => (n === slot.index ? lines : x)));
      setActiveSlot(slot.index);
    }
  };

  const submit = () => {
    if (!complete || readonlyCart) {
      return;
    }
    // One qty 1 line per unit, repeated for each deal ordered
    const lines: CartLine[] = [];
    for (let n = 0; n < qty; n++) {
      for (const line of picks.flat()) {
        for (let u = 0; u < line.item.qty; u++) {
          lines.push(copyLine(line));
        }
      }
    }
    addOrderLines(lines, replaceLineIds ?? []);
    onClose();
  };

  const nextSlot = activeSlot != null ? deal.slots[activeSlot] : null;
  const ctaLabel = complete
    ? `${isEdit ? "Update combo" : "Add to cart"} - ${formatAmount(summary.total * qty)}`
    : nextSlot
      ? nextSlot.qty - picks[nextSlot.index].length > 1 ||
        picks[nextSlot.index].length > 0
        ? `Choose ${countOf(nextSlot.label, nextSlot.qty - picks[nextSlot.index].length).replace(" ", " more ")}`
        : `Choose ${/^[aeiou]/i.test(nextSlot.label) ? "an" : "a"} ${nextSlot.label.toLowerCase()}`
      : "";

  const instruction = useMemo(
    () =>
      `Pick ${deal.slots
        .map((s) =>
          s.qty > 1
            ? countOf(s.label, s.qty)
            : `${/^[aeiou]/i.test(s.label) ? "an" : "a"} ${s.label.toLowerCase()}`,
        )
        .join(", ")
        .replace(/, ([^,]*)$/, " and $1")}`,
    [deal],
  );

  const pickText = (line: CartLine) => {
    const extras = getTotalPrice(line.item) - line.item.product.price;
    const modifiers = getModifiersList(line.item).replace(/\n/g, ", ");
    return { extras, modifiers };
  };

  const slotCard = (slot: DealSlot) => {
    const slotPicks = picks[slot.index];
    const done = slotPicks.length === slot.qty;
    return (
      <div key={slot.index} className={styles.slotCard}>
        <div className={styles.slotCardText}>
          <span className={styles.slotCardLabel}>
            {slot.label}
            {done && !complete ? " · Done" : ""}
          </span>
          {slotPicks.map((line) => {
            const { extras, modifiers } = pickText(line);
            return (
              <span key={line.id} className={styles.slotCardPick}>
                <b>{line.item.product.name}</b>
                {modifiers && <span> · {modifiers}</span>}
                {extras > 0 && <span> +{formatAmount(extras)}</span>}
              </span>
            );
          })}
        </div>
        <button
          type="button"
          className={styles.link}
          style={{ color: brand }}
          onClick={() => setActiveSlot(slot.index)}
        >
          Change
        </button>
      </div>
    );
  };

  const slotOptions = (slot: DealSlot) => {
    const slotPicks = picks[slot.index];
    const multi = slot.qty > 1;
    return (
      <section key={slot.index} className={styles.slot}>
        <div
          className={styles.slotHeader}
          style={{
            backgroundColor:
              template.productScreen.modifiers.headerBackgroundColour,
            color: template.productScreen.modifiers.headerFontColour,
          }}
        >
          <span>{slot.label}</span>
          <span className={styles.slotRule}>
            Choose {slot.qty} · {slotPicks.length}/{slot.qty}
          </span>
        </div>
        <ul
          className={styles.options}
          role={multi ? "group" : "radiogroup"}
          aria-label={slot.label}
        >
          {slot.options.map(({ menuItem, price, soldOut }) => {
            const product = menuItem.productData;
            const count = slotPicks.filter(
              (x) => x.item.productCode === menuItem.productCode,
            ).length;
            const selected = count > 0;
            const full = slotPicks.length >= slot.qty;
            return (
              <li
                key={menuItem.productCode}
                className={clsx(
                  styles.option,
                  soldOut && styles.optionUnavailable,
                  selected && styles.optionSelected,
                )}
                style={
                  selected
                    ? ({ ["--option-brand"]: brand } as React.CSSProperties)
                    : undefined
                }
              >
                <button
                  type="button"
                  className={styles.optionMain}
                  disabled={soldOut || (multi && full && !selected)}
                  onClick={() =>
                    multi
                      ? !full && choose(slot, menuItem, false)
                      : choose(slot, menuItem, true)
                  }
                  role={multi ? undefined : "radio"}
                  aria-checked={multi ? undefined : selected}
                >
                  <span className={styles.thumb}>
                    {product.image && (
                      <img
                        src={uploadcareConfig.getUrl(
                          product.image.fileId,
                          product.image.modifiers,
                        )}
                        alt=""
                      />
                    )}
                  </span>
                  <span className={styles.optionText}>
                    <span className={styles.optionName}>{product.name}</span>
                    {soldOut ? (
                      <span className={styles.soldOut}>Sold out</span>
                    ) : (
                      <span className={styles.optionPrice}>
                        Included · {formatAmount(price)} separately
                      </span>
                    )}
                  </span>
                  {!multi && (
                    <span
                      className={clsx(styles.radio, selected && styles.radioOn)}
                      aria-hidden="true"
                    />
                  )}
                </button>
                {multi && !soldOut && (
                  <span className={styles.stepper}>
                    {count > 0 && (
                      <>
                        <button
                          type="button"
                          className={styles.stepperButton}
                          aria-label={`Remove one ${product.name}`}
                          onClick={() => removeOne(slot, menuItem.productCode)}
                        >
                          <MinusIcon />
                        </button>
                        <span>{count}</span>
                      </>
                    )}
                    <button
                      type="button"
                      className={clsx(styles.stepperButton, styles.stepperAdd)}
                      style={{ backgroundColor: brand, borderColor: brand }}
                      aria-label={`Add ${product.name}`}
                      disabled={full}
                      onClick={() => choose(slot, menuItem, false)}
                    >
                      <PlusIcon />
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        {multi && (
          <p className={styles.hint}>
            You can pick the same item more than once.
          </p>
        )}
      </section>
    );
  };

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={deal.name}
    >
      {optionsFor && (
        <ProductModal
          isInsert
          isForPromoModal
          initLine={optionsFor.line}
          onClose={() => setOptionsFor(null)}
          onSave={(line) => {
            addPick(optionsFor.slot, line, optionsFor.replace);
            setOptionsFor(null);
          }}
          saveLabel={(line) => {
            const extras = getTotalPrice(line.item) - line.item.product.price;
            return extras > 0
              ? `Add to combo · +${formatAmount(extras)}`
              : "Add to combo";
          }}
        />
      )}
      <div className={styles.modal}>
        <header className={styles.header}>
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Back"
            onClick={onClose}
          >
            <BackChevronIcon />
          </button>
          <h2 className={styles.title}>{deal.name}</h2>
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>

        <div className={styles.body}>
          <aside className={styles.rail}>
            <div className={styles.summary}>
              <span>{complete ? "All set" : instruction}</span>
              <b>{formatAmount(deal.price)}</b>
            </div>
            <div className={styles.progress} aria-hidden="true">
              {deal.slots.map((s) => (
                <span key={s.index} className={styles.progressTrack}>
                  <span
                    className={styles.progressFill}
                    style={{
                      width: `${(picks[s.index].length / s.qty) * 100}%`,
                      backgroundColor: brand,
                    }}
                  />
                </span>
              ))}
            </div>
            <div className={styles.slotCards}>
              {deal.slots
                .filter(
                  (s) => picks[s.index].length > 0 && s.index !== activeSlot,
                )
                .map(slotCard)}
            </div>
            {complete && (
              <dl className={styles.breakdown}>
                <div>
                  <dt>Deal price</dt>
                  <dd>{formatAmount(deal.price)}</dd>
                </div>
                {summary.extras > 0 && (
                  <div>
                    <dt>Extras</dt>
                    <dd>+{formatAmount(summary.extras)}</dd>
                  </div>
                )}
                <div>
                  <dt>Separately</dt>
                  <dd>
                    {summary.saving > 0 ? (
                      <s>{formatAmount(summary.separately)}</s>
                    ) : (
                      formatAmount(summary.separately)
                    )}
                  </dd>
                </div>
                {summary.saving > 0 ? (
                  <div
                    className={styles.saveBand}
                    style={{
                      backgroundColor: savingBackground,
                      color: savingColour,
                    }}
                  >
                    <dt>You save</dt>
                    <dd>{formatAmount(summary.saving)}</dd>
                  </div>
                ) : (
                  <p className={styles.noSaving}>
                    These picks cost less separately, so you&apos;ll pay the
                    separate price.
                  </p>
                )}
              </dl>
            )}
          </aside>

          <div className={styles.slots}>
            {activeSlot != null && slotOptions(deal.slots[activeSlot])}
          </div>
        </div>

        <footer className={styles.footer}>
          {complete ? (
            <div className={styles.footerActions}>
              {!isEdit && (
                <span className={styles.qty}>
                  <Button
                    variant="counter-secondary"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    disabled={qty === 1}
                  >
                    <MinusIcon />
                  </Button>
                  <span aria-live="polite">{qty}</span>
                  <Button
                    variant="counter-primary"
                    onClick={() => setQty(qty + 1)}
                  >
                    <PlusIcon />
                  </Button>
                </span>
              )}
              <Button onClick={submit} disabled={readonlyCart}>
                {ctaLabel}
              </Button>
            </div>
          ) : (
            <>
              <div className={styles.running}>
                <span>
                  {chosen} of {needed} chosen
                </span>
                <span>
                  {formatAmount(deal.price)}
                  {summary.extras > 0 &&
                    ` + ${formatAmount(summary.extras)} extras`}
                </span>
              </div>
              <Button disabled>{ctaLabel}</Button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default DealBuilder;
